import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Calculator, IndianRupee, Zap } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSEO } from "@/lib/useSEO";

const DEFAULT = {
  total_pages: 120,
  bw_pages: 100,
  color_pages: 20,
  copies: 1,
  paper_gsm: "80 GSM",
  paper_size: "A4",
  print_side: "single",
  binding: "spiral",
  golden_embossing: false,
  lamination: false,
  delivery: "local",
  urgent: false,
};

const RATE_HINTS = [
  ["B/W A4 (75–90 GSM)", "₹1.50 / page"],
  ["B/W A4 (100 GSM)", "₹2.00 / page"],
  ["Colour A4 (75–90 GSM)", "₹5.00 / page"],
  ["Colour A4 (100 GSM)", "₹6.00 / page"],
  ["Spiral (≤100 pgs)", "₹30"],
  ["Spiral (101–150 pgs)", "₹50"],
  ["Spiral (151–200 pgs)", "₹80"],
  ["Soft binding", "₹100"],
  ["Hard binding", "₹200"],
  ["Leather hard binding", "₹350"],
  ["Golden embossing", "₹50"],
  ["Lamination", "₹50"],
  ["Packaging", "₹40"],
  ["Local delivery (Lucknow)", "₹80"],
  ["India delivery", "₹150"],
  ["Express delivery", "₹300"],
  ["Urgent surcharge", "₹250"],
];

export default function PriceCalculator() {
  useSEO({
    title: "Printing Cost Calculator — Thesis Print, Hard Binding, Golden Embossing · ScholarPrintHub",
    description: "Live cost calculator for thesis printing, hard binding, golden embossing, lamination, and doorstep delivery across India. Transparent per-page and per-binding pricing.",
    keywords: "thesis printing cost, hard binding price, golden embossing cost, print cost calculator india, spiral binding price, dissertation printing rate",
  });
  const [form, setForm] = useState(DEFAULT);
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));

  const runEstimate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/calculator/estimate", form);
      setEstimate(data);
    } catch (e) {
      toast.error("Estimate failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const total = estimate?.total ?? null;

  useEffect(() => {
    // Auto-run whenever form changes (debounced)
    const id = setTimeout(runEstimate, 200);
    return () => clearTimeout(id);
  }, [form]);

  return (
    <div data-testid="calculator-page" className="container-app py-16">
      <span className="section-eyebrow">Tool · Live pricing</span>
      <div className="divider-gold mt-2 mb-4" />
      <h1 className="font-display text-4xl sm:text-5xl text-navy">Printing Cost Calculator</h1>
      <p className="mt-3 text-slate-600 max-w-2xl">Adjust the options — the running total updates in real time.</p>

      <div className="grid lg:grid-cols-12 gap-8 mt-10">
        {/* Inputs */}
        <div className="lg:col-span-8 border border-slate-200 rounded-sm p-6 bg-white">
          <div className="grid sm:grid-cols-3 gap-4">
            <NumField label="Total pages" value={form.total_pages} onChange={(v) => set("total_pages")(v)} testid="calc-total-pages" />
            <NumField label="B/W pages" value={form.bw_pages} onChange={(v) => set("bw_pages")(v)} testid="calc-bw-pages" />
            <NumField label="Colour pages" value={form.color_pages} onChange={(v) => set("color_pages")(v)} testid="calc-color-pages" />
            <NumField label="Copies" value={form.copies} min={1} onChange={(v) => set("copies")(v)} testid="calc-copies" />
            <SelectField label="Paper size" value={form.paper_size} onChange={set("paper_size")} testid="calc-paper-size" options={["A4", "A5"]} />
            <SelectField label="Paper GSM" value={form.paper_gsm} onChange={set("paper_gsm")} testid="calc-paper-gsm" options={["70 GSM", "75 GSM", "80 GSM", "100 GSM"]} />
            <SelectField label="Printing side" value={form.print_side} onChange={set("print_side")} testid="calc-print-side" options={[["single", "Single side"], ["double", "Double side"]]} />
            <SelectField label="Binding" value={form.binding} onChange={set("binding")} testid="calc-binding" options={[
              ["none", "No binding"], ["spiral", "Spiral"], ["soft", "Soft binding"], ["hard", "Hard binding"], ["leather", "Leather hard binding"],
            ]} />
            <SelectField label="Delivery" value={form.delivery} onChange={set("delivery")} testid="calc-delivery" options={[
              ["local", "Local (Lucknow)"], ["standard", "Standard India"], ["express", "Express"],
            ]} />
          </div>

          <div className="grid sm:grid-cols-3 gap-6 mt-6 pt-6 border-t border-slate-200">
            <ToggleField label="Golden embossing" testid="calc-emboss" checked={form.golden_embossing} onChange={(v) => set("golden_embossing")(v)} />
            <ToggleField label="Lamination" testid="calc-lamination" checked={form.lamination} onChange={(v) => set("lamination")(v)} />
            <ToggleField label="Urgent order" testid="calc-urgent" checked={form.urgent} onChange={(v) => set("urgent")(v)} />
          </div>
        </div>

        {/* Totals sticky */}
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-24 border border-slate-200 rounded-sm bg-navy text-white p-6" data-testid="calc-summary">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-widest text-gold">Live estimate</span>
              <Calculator className="w-4 h-4 text-gold" />
            </div>
            <div className="mt-4 flex items-center gap-1">
              <IndianRupee className="w-6 h-6 text-gold" />
              <span data-testid="calc-total" className={`font-display text-5xl ${loading ? "opacity-60" : ""}`}>
                {total == null ? "—" : Math.round(total).toLocaleString("en-IN")}
              </span>
            </div>
            <div className="mt-1 text-xs text-slate-300">All-inclusive · GST extra where applicable</div>

            {estimate && (
              <ul className="mt-6 space-y-1 text-xs text-slate-200">
                <LineItem label="B/W printing" value={estimate.bw_cost} />
                <LineItem label="Colour printing" value={estimate.color_cost} />
                <LineItem label="Binding" value={estimate.binding_cost} />
                <LineItem label="Golden embossing" value={estimate.embossing_cost} />
                <LineItem label="Lamination" value={estimate.lamination_cost} />
                <LineItem label="Packaging" value={estimate.packaging_cost} />
                <LineItem label="Delivery" value={estimate.delivery_cost} />
                <LineItem label="Urgent surcharge" value={estimate.urgent_cost} />
              </ul>
            )}

            <a href="/upload" className="btn-gold w-full mt-6 flex justify-center">
              <Zap className="w-4 h-4" /> Continue to upload
            </a>
          </div>

          <div className="border border-slate-200 rounded-sm p-5 mt-4 text-xs text-slate-600">
            <div className="section-eyebrow mb-2">Reference rates</div>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-1">
              {RATE_HINTS.map(([k, v]) => (<li key={k} className="flex justify-between"><span>{k}</span><span className="font-mono text-navy">{v}</span></li>))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function NumField({ label, value, onChange, min = 0, testid }) {
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <Input type="number" min={min} data-testid={testid} value={value}
             onChange={(e) => onChange(parseInt(e.target.value || "0", 10))}
             className="mt-1 h-10" />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  const norm = options.map((o) => Array.isArray(o) ? o : [o, o]);
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="mt-1 h-10"><SelectValue /></SelectTrigger>
        <SelectContent>
          {norm.map(([v, l]) => (<SelectItem key={v} value={v}>{l}</SelectItem>))}
        </SelectContent>
      </Select>
    </div>
  );
}

function ToggleField({ label, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between border border-slate-200 rounded-sm px-3 py-2">
      <div className="text-sm text-slate-700">{label}</div>
      <Switch data-testid={testid} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

function LineItem({ label, value }) {
  if (!value) return null;
  return <li className="flex justify-between"><span>{label}</span><span className="font-mono">₹{value}</span></li>;
}
